import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BetPlacementService } from "../../../../../../../shared/src/lib/services/bet-placement.service";
import { AlertComponent } from "../../../../components/alert/alert.component";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import { ReportUtilsService } from "../report-utils.service";
import { downloadTableAsExcel } from "../../../../../../../shared/src/lib/utils/common";
import { Medium24Pipe } from "../../../../../../../shared/src/lib/pipe/medium-24.pipe";
import { LoaderService } from "../../../../../../../shared/src/lib/services/loader.service";
import { Observable, Subject } from "rxjs";
import { ReportLoaderComponent } from "../report-loader/report-loader.component";
import { MatButton } from "@angular/material/button";
import { NgxPrintDirective } from "ngx-print";
import { MatTooltipModule } from "@angular/material/tooltip";
import { genericSort } from "../../../../../../../shared/src/lib/utils/market";
import { takeUntil } from "rxjs/operators";
import { isMoreThanNDaysOld } from "../../../../../../../shared/src/lib/utils/date-range";

@Component({
  selector: "app-bets",
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    DecimalPipe,
    AlertComponent,
    MatProgressBarModule,
    NgClass,
    RouterLink,
    MatPaginatorModule,
    MatIconModule,
    TranslateModule,
    AsyncPipe,
    Medium24Pipe,
    ReportLoaderComponent,
    MatButton,
    NgxPrintDirective,
    MatTooltipModule,
  ],
  templateUrl: "./bets.component.html",
  styleUrls: ["./bets.component.scss", "../report.scss"],
})
export class BetsComponent implements OnInit, OnDestroy {
  betService = inject(BetPlacementService);
  loader = inject(LoaderService);
  reportUtils = inject(ReportUtilsService);
  fb = inject(FormBuilder);
  router = inject(Router);
  route = inject(ActivatedRoute);

  readonly destroy$ = new Subject<void>();
  currentBets: any[] = [];
  bets: any[] = [];
  $loadingCurrent!: Observable<boolean>;
  $loadingPast!: Observable<boolean>;
  form: FormGroup;
  columns = [
    { title: "Bet ID", selector: "ID" },
    { title: "Matched Date & Time", selector: "MATCHED_ON" },
    { title: "Description", selector: "EVENT_NAME" },
    { title: "Type", selector: "SIDE" },
    { title: "Persistence", selector: "PERSISTENCE_TYPE" },
    { title: "Odds", selector: "ODDS" },
    { title: "Price", selector: "PRICE" },
    { title: "Stake", selector: "SIZE" },
    { title: "Profit / Loss", selector: "PL" },
    { title: "Status", selector: "RESULT" },
  ];

  totalCount = 0;
  page = 1;
  pageSize = 50;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 20, 50, 100];
  currentSort: { field: string; order: "asc" | "desc" } | null = null;

  constructor() {
    this.form = this.fb.group({
      status: [this.route.snapshot.queryParams["status"] || "MATCHED"],
      period: [this.route.snapshot.queryParams["period"] || "CURRENT"],
      RANGE: [+this.route.snapshot.queryParams["RANGE"] || 3],
      FROM_DATE: [this.route.snapshot.queryParams["FROM_DATE"]],
      TO_DATE: [this.route.snapshot.queryParams["TO_DATE"]],
    });
  }

  get range() {
    return this.form.get("RANGE");
  }

  get period() {
    return this.form.get("period");
  }

  ngOnInit() {
    this.$loadingCurrent = this.loader.isLoading("/bf/current-user-order");
    this.$loadingPast = this.loader.isLoading("/v1/api/reports/my_bets");

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      const switchedToPast = values.period === "PAST" && ["MATCHED", "UNMATCHED", "PENDING"].includes(values.status);
      const switchedToCurrent = values.period === "CURRENT" && ["SETTLED", "CANCELLED", "LAPSED", "VOIDED"].includes(values.status);

      if (switchedToPast) {
        this.updateQueryParam({ ...values, status: "SETTLED" });
        this.form.patchValue({ status: "SETTLED" });
        const RANGE = values?.RANGE;
        if (RANGE) {
          const range = this.reportUtils.dateRange(RANGE);
          this.form.patchValue(range);
        }
        this.switchRange(true);
      } else if (switchedToCurrent) {
        this.currentBets = [];
        this.updateQueryParam({ ...values, status: "MATCHED" });
        this.form.patchValue({ status: "MATCHED" });
        this.switchRange(false);
      } else {
        this.updateQueryParam(values);
      }
    });

    this.range?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value !== 8) {
        const range = this.reportUtils.dateRange(value);
        this.form.patchValue(range);
      }
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params: any) => {
      if (params["period"] && params["status"]) {
        const period = params["period"];
        const status = params["status"];
        const DATE_FROM = params["FROM_DATE"];
        const DATE_TO = params["TO_DATE"];
        const RANGE = +params["RANGE"];

        if (period === "CURRENT" && ["MATCHED", "UNMATCHED", "PENDING"].includes(status)) {
          this.switchRange(false);
          if (this.currentBets.length > 0) {
            this.filterCurrentBets(status);
          } else {
            this.getPendingBets();
          }
        } else {
          this.getBetsByStatus({ STATUS: status, RANGE, DATE_FROM, DATE_TO });
        }
      } else {
        this.updateQueryParam({ status: "MATCHED", period: "CURRENT" });
      }
    });

    this.getCurrentBets();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  switchRange(value: boolean) {
    if (value) {
      this.range?.enable({ emitEvent: false });
    } else {
      this.range?.disable({ emitEvent: false });
    }
    this.range?.updateValueAndValidity();
  }

  updateQueryParam(values: any) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: values,
      queryParamsHandling: "merge",
    });
  }

  filterCurrentBets(status: string) {
    this.bets = this.currentBets?.filter((bet) => {
      const notPending = bet.status !== "PENDING";
      if (status === "MATCHED") {
        return parseFloat(bet.stakeRemaining) === 0 && parseFloat(bet.stakeMatched) > 0 && notPending;
      } else if (status === "UNMATCHED") {
        return parseFloat(bet.stakeRemaining) > 0 && parseFloat(bet?.stakeMatched) >= 0 && parseFloat(bet?.sizeLapsed) === 0 && notPending;
      } else {
        return bet.status === "PENDING";
      }
    });
  }

  getBetsByStatus(params?: any) {
    const { RANGE, ...rest } = params || {};
    if (rest?.DATE_FROM && isMoreThanNDaysOld(rest?.DATE_FROM)) return;
    this.betService
      .fetchBetsByStatus(rest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.result) {
            const sortReadyBets = response?.result?.map((bet: any) => ({
              ...bet,
              ODDS: parseFloat(bet?.ODDS),
              DF_ODDS: parseFloat(bet?.DF_ODDS),
              SIZE: parseFloat(bet?.SIZE),
              PL: parseFloat(bet?.PL),
              PERSISTENCE_TYPE: this.persistenceType(bet?.PERSISTENCE_TYPE),
            }));
            this.bets = sortReadyBets;
            this.currentBets = sortReadyBets;
            this.setPagination(sortReadyBets?.length);
          }
        },
      });
  }

  getPendingBets() {
    this.betService.setRefresh(true);
  }

  getCurrentBets() {
    this.betService.openBetsSubject.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: any) => {
        if (result && this.isCurrent) {
          const sortReadyBets = result?.map((bet: any) => ({
            ...bet,
            profitLoss: parseFloat(this.profitLoss(bet)),
            priceSizePrice: parseFloat(bet?.priceSize?.price),
            betSide: this.side(bet),
            betSize: parseFloat(bet?.betSize),
            averagePriceMatched: parseFloat(bet?.averagePriceMatched),
            persistenceType: this.persistenceType(bet?.persistenceType),
          }));
          this.bets = sortReadyBets;
          this.currentBets = sortReadyBets;
          this.filterCurrentBets(this.route.snapshot.queryParams["status"]);
          this.setPagination(sortReadyBets?.length);
        }
      },
    });
  }

  profitLoss(item: any) {
    return item?.side == "BACK" ? item?.potentialProfit : item?.potentialLoss;
  }

  filterColumns(columns: any[]) {
    if (this.isCurrent) {
      if (this.isUnmatched || this.isPending) {
        return columns.filter((item) => !["Matched Date & Time"].includes(item?.title));
      }
    }

    if (this.isPastNotSettled) {
      return columns.filter((item) => !["Status", "Profit / Loss"].includes(item?.title));
    }

    if (this.isPastSettled) {
      return columns.filter((item) => item?.title !== "Status");
    }

    return columns;
  }

  get isPastNotSettled() {
    return this.isPast && this.isNotSettled;
  }

  get isPastSettled() {
    return this.isPast && this.isSettled;
  }

  get isCurrent() {
    return this.form.value.period === "CURRENT";
  }

  get isMatched() {
    return this.form.value.status === "MATCHED";
  }

  get isPending() {
    return this.form.value.status === "PENDING";
  }

  get isUnmatched() {
    return this.form.value.status === "UNMATCHED";
  }

  get isPast() {
    return this.form.value.period === "PAST";
  }

  get isSettled() {
    return this.form.value.status === "SETTLED";
  }

  get isNotSettled() {
    return ["CANCELLED", "LAPSED", "VOIDED"].includes(this.form.value.status);
  }

  persistenceType(value: string) {
    return value === "LAPSE" ? "C" : "K";
  }

  side(bet: any) {
    if (!bet) return undefined; // Ensure bet is defined to avoid errors

    if (bet.bettingType === "LINE") {
      return bet.descriptionPrefix === "Fancy" ? (bet.side === "BACK" ? "YES" : "NO") : bet.side === "BACK" ? "UNDER" : "OVER";
    }

    return bet.SIDE ?? bet.side;
  }

  handleDownload() {
    downloadTableAsExcel("bets-table", "my bets report");
  }

  handlePageChange(e: any) {
    this.pageIndex = e?.pageSize !== this.pageSize ? 0 : e?.pageIndex;
    this.page = this.pageIndex + 1;
    this.pageSize = e?.pageSize;
  }

  setPagination(count: number) {
    this.totalCount = count;
    this.pageIndex = 0;
    this.page = 1;
  }

  get paginatedBets() {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    return this.bets.slice(start, end);
  }

  trackBy(bet: any) {
    return bet?.betId ?? bet?.BF_BET_ID;
  }

  getSortField(field: string) {
    if (field === "ID") return this.isPast ? field : "customerOrderRef";
    if (field === "MATCHED_ON") return this.isPast ? (this.isPastSettled ? "SETTLED_ON" : "PLACED_ON") : "matchedDate";
    if (field === "SIDE") return this.isPast ? field : "betSide";
    if (field === "PERSISTENCE_TYPE") return this.isPast ? field : "persistenceType";
    if (field === "ODDS") return this.isPast ? field : this.isMatched ? "averagePriceMatched" : this.isPending ? "price" : "priceSizePrice";
    if (field === "PRICE") return this.isPast ? "DF_ODDS" : "dfOdds";
    if (field === "SIZE") return this.isPast ? field : "betSize";
    if (field === "PL") return this.isPast ? field : "profitLoss";

    return field;
  }

  onSort(field: string) {
    if (field === "EVENT_NAME") return;
    const sortField = this.getSortField(field);
    if (!this.currentSort || this.currentSort.field !== sortField) {
      this.currentSort = { field: sortField, order: "asc" };
    } else if (this.currentSort.order === "asc") {
      this.currentSort.order = "desc";
    } else {
      this.currentSort = null;
    }

    if (this.currentSort) {
      const sortFields = [this.currentSort.field];
      this.handleSort([...this.bets], sortFields, this.currentSort?.order);
    } else if (this.isPast) {
      this.bets = this.currentBets;
    } else {
      this.filterCurrentBets(this.route.snapshot.queryParams["status"]);
    }
  }

  handleSort(items: any[], sortFields: string[], sortOrder?: "asc" | "desc") {
    this.bets = genericSort(items, sortFields, sortOrder);
  }

  betPrice(bet: any) {
    if (bet?.DF_ODDS) return bet?.DF_ODDS;

    if (this.isFancyOrBm(bet?.descriptionPrefix)) return bet?.dfOdds;

    if (this.isMatched) {
      return bet?.averagePriceMatched;
    } else if (this.isPending) {
      return bet?.price;
    } else {
      return bet?.priceSize?.price;
    }
  }

  betOdds(bet: any) {
    if (bet?.ODDS) return bet?.ODDS;

    if (this.isMatched) {
      return bet?.averagePriceMatched;
    } else if (this.isPending) {
      return bet?.price;
    } else {
      return bet?.priceSize?.price;
    }
  }

  displayStake(bet: any) {
    if (bet?.SIZE) {
      const stakeMatched = parseFloat(bet?.STAKE_MATCHED);
      return stakeMatched > 0 ? stakeMatched : bet?.SIZE;
    }
    return bet?.stakeMatched > 0 && bet?.provider === "BF" && bet?.stakeRemaining === 0 ? bet?.stakeMatched : bet?.betSize;
  }

  isFancyOrBm(prefix: string) {
    return ["Fancy", "Bookmaker"].includes(prefix);
  }
}
